// 工種 / 種別 / 工程 を 3 層ネストアコーディオンで一画面に統合するマスタ編集画面。
// モックアップ rev2.20 仕様を移植。データ層は Web 規約 (deleted_at / updatedAt / replaceCollection*) に対応。

import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Stack, Typography, Paper, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell,
  Accordion, AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EngineeringIcon from '@mui/icons-material/Engineering';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScienceIcon from '@mui/icons-material/Science';
import { ExtendedDB } from '../../../types/extendedDB';
import { useFullDb, replaceCollection, replaceCollections, nextIdFor } from '../../../api/dbSlice';
import { MasterAccordionSummary } from './MasterAccordionSummary';
import { useIsOnline } from '../../../util/useIsOnline';
import { OfflineMasterBanner } from '../../../components/OfflineMasterBanner';
import { usePartitionFilter } from '../../../util/partitionFilterStore';
import type { WorkTypeBase } from '../../../schema/generated/WorkType';
import type { WorkSubtypeBase } from '../../../schema/generated/WorkSubtype';
import type { WorkDetailBase } from '../../../schema/generated/WorkDetail';
import type { MixRatioDefinitionBase } from '../../../schema/generated/MixRatioDefinition';
import { PageContext } from '../PageContext';
import { mixRatioDisplayName } from '../../../util/mixRatioLabel';
import { WORD } from '../../../word';
import { renderWord } from '../../../util/renderWord';
import Time from '../../../util/Time';
import { EmptyState } from '../../../components/EmptyState';
import { errorMessageOf } from "../../../util/typed";
import { ContentLoading } from "../../../components/Loading";

type WorkType = WorkTypeBase;
type WorkSubtype = WorkSubtypeBase;
type WorkDetail = WorkDetailBase;

function mixRatioOptions(db: ExtendedDB) {
  return (db.mixRatioDefinitions ?? [])
    .filter((m) => m.deleted_at == null)
    .map((m) => ({ value: m.id, label: mixRatioDisplayName(db, m) }));
}

function mixRatioLabel(db: ExtendedDB, mixRatioId: number | undefined | null): string {
  if (mixRatioId == null) return '';
  const m = (db.mixRatioDefinitions ?? []).find((x: MixRatioDefinitionBase) => x.id === mixRatioId);
  if (!m) return '';
  return mixRatioDisplayName(db, m);
}

// 既存なら置換、無ければ末尾追加。更新時は updatedAt をバンプして dbSlice 側の差分検知に乗せる。
function upsertById<T extends { id: number; updatedAt: number }>(cur: T[], next: T): T[] {
  const now = Time.Now().GetTime();
  const nextWithTs = { ...next, updatedAt: now };
  if (cur.some((x) => x.id === next.id)) {
    return cur.map((x) => (x.id === next.id ? nextWithTs : x));
  }
  return [...cur, nextWithTs];
}

// ===== 編集ダイアログ =====
function MixRatioSelector({ db, value, onChange }:
  { db: ExtendedDB; value: number | null | undefined; onChange: (next: number | null) => void }) {
  return (
    <TextField select label="配合比" size="small" fullWidth
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      helperText={WORD.workClassification.hMixRatio}>
      <MenuItem value=""><em>(未設定)</em></MenuItem>
      {mixRatioOptions(db).map((o) => (
        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
      ))}
    </TextField>
  );
}

function WorkTypeDialog({ draft, isNew, isOnline, onSave, onClose }:
  { draft: WorkType; isNew: boolean; isOnline: boolean; onSave: (next: WorkType) => void; onClose: () => void }) {
  const [d, setD] = useState<WorkType>(draft);
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? '工種を新規追加' : '工種を編集'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField label="工種名" size="small" fullWidth required
            value={d.name ?? ''} onChange={(e) => setD({ ...d, name: e.target.value })} />
          <TextField label="表示順" size="small" type="number"
            value={d.display_order ?? 99} onChange={(e) => setD({ ...d, display_order: Number(e.target.value) })} />
          <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.workClassification.workTypeDialogInfo)}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button variant="contained" disabled={!isOnline || !d.name} onClick={() => onSave(d)}>保存</Button>
      </DialogActions>
    </Dialog>
  );
}

function WorkSubtypeDialog({ draft, isNew, isOnline, workTypes, workDetails, onSave, onClose }:
  { draft: WorkSubtype; isNew: boolean; isOnline: boolean; workTypes: WorkType[]; workDetails: WorkDetail[]; onSave: (next: WorkSubtype) => void; onClose: () => void }) {
  const [d, setD] = useState<WorkSubtype>(draft);
  const subordinateDetails = workDetails.filter((wd) => wd.work_subtype_id === d.id);
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? '種別を新規追加' : '種別を編集'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField select label="工種" size="small" required
            value={d.work_type_id ?? ''}
            onChange={(e) => setD({ ...d, work_type_id: Number(e.target.value) })}>
            {workTypes.map((wt) => <MenuItem key={wt.id} value={wt.id}>{wt.name}</MenuItem>)}
          </TextField>
          <TextField label="種別名" size="small" fullWidth required
            value={d.name ?? ''} onChange={(e) => setD({ ...d, name: e.target.value })} />
          <TextField label="単位" size="small" fullWidth
            value={d.unit ?? ''} onChange={(e) => setD({ ...d, unit: e.target.value })}
            helperText={WORD.mastersConfigs.hWorkSubtypeUnit} />
          <TextField select label="代表工程" size="small"
            value={d.primary_work_detail_id ?? ''}
            onChange={(e) => setD({ ...d, primary_work_detail_id: e.target.value === '' ? null : Number(e.target.value) })}
            helperText={WORD.mastersConfigs.hWorkSubtypePrimaryDetail}>
            <MenuItem value=""><em>(未設定: レイヤー数比率で按分)</em></MenuItem>
            {subordinateDetails.map((wd) => <MenuItem key={wd.id} value={wd.id}>{wd.name}</MenuItem>)}
          </TextField>
          <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.workClassification.workSubtypeDialogInfo)}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button variant="contained" disabled={!isOnline || !d.name || !d.work_type_id || !d.unit} onClick={() => onSave(d)}>保存</Button>
      </DialogActions>
    </Dialog>
  );
}

function WorkDetailDialog({ draft, isNew, isOnline, workTypes, workSubtypes, db, onSave, onClose }:
  { draft: WorkDetail; isNew: boolean; isOnline: boolean; workTypes: WorkType[]; workSubtypes: WorkSubtype[]; db: ExtendedDB; onSave: (next: WorkDetail) => void; onClose: () => void }) {
  const [d, setD] = useState<WorkDetail>(draft);
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? '工程を新規追加' : '工程を編集'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField select label="種別" size="small" required
            value={d.work_subtype_id ?? ''}
            onChange={(e) => setD({ ...d, work_subtype_id: Number(e.target.value) })}
            helperText={WORD.workClassification.hWorkSubtype}>
            {workSubtypes.map((wst) => {
              const wt = workTypes.find((w) => w.id === wst.work_type_id);
              return <MenuItem key={wst.id} value={wst.id}>{wt?.name ?? '?'} / {wst.name}</MenuItem>;
            })}
          </TextField>
          <TextField label="工程名" size="small" fullWidth required
            value={d.name ?? ''} onChange={(e) => setD({ ...d, name: e.target.value })} />
          <MixRatioSelector db={db} value={d.mix_ratio_definition_id}
            onChange={(v) => setD({ ...d, mix_ratio_definition_id: v })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button variant="contained" disabled={!isOnline || !d.name || !d.work_subtype_id} onClick={() => onSave(d)}>保存</Button>
      </DialogActions>
    </Dialog>
  );
}

// ===== メインページ =====
export default function WorkClassificationPage({ pageContext, showOfflineBanner = true }: { pageContext: PageContext; showOfflineBanner?: boolean }) {
  const idb = pageContext.api.indexedDB;
  const ownerId = pageContext.api.currentUser.id;
  const { db, loading } = useFullDb(idb, ownerId);
  const isOnline = useIsOnline(pageContext.api);

  // ---- 区分フィルタ (area_division_id) ----
  const partitionOptions = useMemo(() => {
    return db.areaDivisions
      .filter((d) => d.is_active && d.deleted_at == null)
      .sort((a, b) => a.display_order - b.display_order)
      .map((d) => ({ value: d.id, label: `#${d.id} ${d.name}` }));
  }, [db.areaDivisions]);

  const [filterValue, setFilterValue] = usePartitionFilter('area_division_id');
  useEffect(() => {
    if (partitionOptions.length === 0) return;
    if (filterValue === null || !partitionOptions.some((o) => o.value === filterValue)) {
      const firstOption = partitionOptions[0];
      if (firstOption !== undefined) {
        setFilterValue(firstOption.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partitionOptions]);

  const allWorkTypes    = (db.workTypes ?? []).filter((w) => w.deleted_at == null);
  const allWorkSubtypes = (db.workSubtypes ?? []).filter((w) => w.deleted_at == null);
  const allWorkDetails  = (db.workDetails ?? []).filter((w) => w.deleted_at == null);

  const visibleWorkTypes = useMemo(
    () => {
      const base = filterValue == null ? allWorkTypes : allWorkTypes.filter((w) => w.area_division_id === filterValue);
      return [...base].sort((a, b) => (a.display_order ?? 99) - (b.display_order ?? 99));
    },
    [allWorkTypes, filterValue]
  );

  const subtypesOf = (workTypeId: number) => allWorkSubtypes.filter((s) => s.work_type_id === workTypeId);
  const detailsOf = (workSubtypeId: number) => allWorkDetails.filter((d) => d.work_subtype_id === workSubtypeId);

  const subtypeMissingMixRatio = (wst: WorkSubtype): boolean =>
    detailsOf(wst.id).some((wd) => wd.mix_ratio_definition_id == null);
  const workTypeMissingMixRatio = (wt: WorkType): boolean =>
    subtypesOf(wt.id).some((wst) => subtypeMissingMixRatio(wst));

  // ---- アコーディオン開閉 ----
  const [expandedWorkType, setExpandedWorkType] = useState<number | null>(null);
  const [expandedSubtype, setExpandedSubtype] = useState<number | null>(null);

  // ---- 編集ステート ----
  const [editingType, setEditingType] = useState<WorkType | null>(null);
  const [editingTypeIsNew, setEditingTypeIsNew] = useState(false);
  const [confirmDeleteType, setConfirmDeleteType] = useState<WorkType | null>(null);

  const [editingSubtype, setEditingSubtype] = useState<WorkSubtype | null>(null);
  const [editingSubtypeIsNew, setEditingSubtypeIsNew] = useState(false);
  const [confirmDeleteSubtype, setConfirmDeleteSubtype] = useState<WorkSubtype | null>(null);

  const [editingDetail, setEditingDetail] = useState<WorkDetail | null>(null);
  const [editingDetailIsNew, setEditingDetailIsNew] = useState(false);
  const [confirmDeleteDetail, setConfirmDeleteDetail] = useState<WorkDetail | null>(null);

  // ---- 工種 CRUD ----
  const openNewType = async () => {
    const newId = await nextIdFor(idb, ownerId, 'workTypes');
    const now = Time.Now().GetTime();
    setEditingType({
      id: newId, createdAt: now, updatedAt: now, deleted_at: null,
      area_division_id: filterValue ?? 100,
      name: '', display_order: 99,
    });
    setEditingTypeIsNew(true);
  };
  const openEditType = (wt: WorkType) => { setEditingType({ ...wt }); setEditingTypeIsNew(false); };
  const saveType = async (next: WorkType) => {
    try {
      const rows = upsertById((db.workTypes ?? []), next);
      await replaceCollection(idb, ownerId, 'workTypes', rows, pageContext.api);
      setEditingType(null);
    } catch (e) {
      pageContext.api.showError(`工種の保存に失敗しました。\n${errorMessageOf(e)}`);
    }
  };
  const duplicateType = async (wt: WorkType) => {
    try {
      const newId = await nextIdFor(idb, ownerId, 'workTypes');
      const now = Time.Now().GetTime();
      const copy: WorkType = { ...wt, id: newId, name: `${wt.name} (コピー)`, createdAt: now, updatedAt: now, deleted_at: null };
      await replaceCollection(idb, ownerId, 'workTypes', [...(db.workTypes ?? []), copy], pageContext.api);
    } catch (e) {
      pageContext.api.showError(`工種の複製に失敗しました。\n${errorMessageOf(e)}`);
    }
  };
  const deleteType = async (wt: WorkType) => {
    try {
      const now = Time.Now().GetTime();
      const curTypes = (db.workTypes ?? []);
      const curSubtypes = (db.workSubtypes ?? []);
      const curDetails = (db.workDetails ?? []);
      const subIds = curSubtypes.filter((s) => s.work_type_id === wt.id).map((s) => s.id);
      await replaceCollections(idb, ownerId, {
        workTypes: curTypes.map((x) => x.id === wt.id ? { ...x, deleted_at: now, updatedAt: now } : x),
        workSubtypes: curSubtypes.map((s) => s.work_type_id === wt.id ? { ...s, deleted_at: now, updatedAt: now } : s),
        workDetails: curDetails.map((d) => subIds.includes(d.work_subtype_id) ? { ...d, deleted_at: now, updatedAt: now } : d),
      }, pageContext.api);
      setConfirmDeleteType(null);
    } catch (e) {
      pageContext.api.showError(`工種の削除に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  // ---- 種別 CRUD ----
  const openNewSubtype = async (workTypeId: number) => {
    const newId = await nextIdFor(idb, ownerId, 'workSubtypes');
    const now = Time.Now().GetTime();
    setEditingSubtype({
      id: newId, createdAt: now, updatedAt: now, deleted_at: null,
      area_division_id: filterValue ?? 100,
      work_type_id: workTypeId, name: '',
      unit: 'm²',
      primary_work_detail_id: null,
    });
    setEditingSubtypeIsNew(true);
  };
  const openEditSubtype = (wst: WorkSubtype) => { setEditingSubtype({ ...wst }); setEditingSubtypeIsNew(false); };
  const saveSubtype = async (next: WorkSubtype) => {
    try {
      const rows = upsertById((db.workSubtypes ?? []), next);
      await replaceCollection(idb, ownerId, 'workSubtypes', rows, pageContext.api);
      setEditingSubtype(null);
    } catch (e) {
      pageContext.api.showError(`種別の保存に失敗しました。\n${errorMessageOf(e)}`);
    }
  };
  const duplicateSubtype = async (wst: WorkSubtype) => {
    try {
      const newId = await nextIdFor(idb, ownerId, 'workSubtypes');
      const now = Time.Now().GetTime();
      const copy: WorkSubtype = { ...wst, id: newId, name: `${wst.name} (コピー)`, createdAt: now, updatedAt: now, deleted_at: null };
      await replaceCollection(idb, ownerId, 'workSubtypes', [...(db.workSubtypes ?? []), copy], pageContext.api);
    } catch (e) {
      pageContext.api.showError(`種別の複製に失敗しました。\n${errorMessageOf(e)}`);
    }
  };
  const deleteSubtype = async (wst: WorkSubtype) => {
    try {
      const now = Time.Now().GetTime();
      const curSubtypes = (db.workSubtypes ?? []);
      const curDetails = (db.workDetails ?? []);
      await replaceCollections(idb, ownerId, {
        workSubtypes: curSubtypes.map((x) => x.id === wst.id ? { ...x, deleted_at: now, updatedAt: now } : x),
        workDetails: curDetails.map((d) => d.work_subtype_id === wst.id ? { ...d, deleted_at: now, updatedAt: now } : d),
      }, pageContext.api);
      setConfirmDeleteSubtype(null);
    } catch (e) {
      pageContext.api.showError(`種別の削除に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  // ---- 工程 CRUD ----
  const openNewDetail = async (workSubtypeId: number) => {
    const newId = await nextIdFor(idb, ownerId, 'workDetails');
    const now = Time.Now().GetTime();
    setEditingDetail({
      id: newId, createdAt: now, updatedAt: now, deleted_at: null,
      area_division_id: filterValue ?? 100,
      work_subtype_id: workSubtypeId, name: '',
      mix_ratio_definition_id: null,
    });
    setEditingDetailIsNew(true);
  };
  const openEditDetail = (wd: WorkDetail) => { setEditingDetail({ ...wd }); setEditingDetailIsNew(false); };
  const saveDetail = async (next: WorkDetail) => {
    try {
      const rows = upsertById((db.workDetails ?? []), next);
      await replaceCollection(idb, ownerId, 'workDetails', rows, pageContext.api);
      setEditingDetail(null);
    } catch (e) {
      pageContext.api.showError(`工程の保存に失敗しました。\n${errorMessageOf(e)}`);
    }
  };
  const duplicateDetail = async (wd: WorkDetail) => {
    try {
      const newId = await nextIdFor(idb, ownerId, 'workDetails');
      const now = Time.Now().GetTime();
      const copy: WorkDetail = { ...wd, id: newId, name: `${wd.name} (コピー)`, createdAt: now, updatedAt: now, deleted_at: null };
      await replaceCollection(idb, ownerId, 'workDetails', [...(db.workDetails ?? []), copy], pageContext.api);
    } catch (e) {
      pageContext.api.showError(`工程の複製に失敗しました。\n${errorMessageOf(e)}`);
    }
  };
  const deleteDetail = async (wd: WorkDetail) => {
    try {
      const now = Time.Now().GetTime();
      const cur = (db.workDetails ?? []);
      await replaceCollection(idb, ownerId, 'workDetails', cur.map((x) => x.id === wd.id ? { ...x, deleted_at: now, updatedAt: now } : x), pageContext.api);
      setConfirmDeleteDetail(null);
    } catch (e) {
      pageContext.api.showError(`工程の削除に失敗しました。\n${errorMessageOf(e)}`);
    }
  };

  if (loading) {
    return <ContentLoading />;
  }

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <EngineeringIcon color="action" />
        <Typography variant="h6" fontWeight="bold">工種 / 種別 / 工程</Typography>
        <Chip size="small" color="info" label={`${visibleWorkTypes.length} 工種`} />
      </Stack>

      {showOfflineBanner && <OfflineMasterBanner isOnline={isOnline} />}

      <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.workClassification.listInfo)}</Alert>

      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body2">工事型区分 で絞り込み:</Typography>
        <TextField select size="small" sx={{ minWidth: 260 }}
          value={filterValue ?? ''}
          onChange={(e) => setFilterValue(e.target.value === '' ? null : Number(e.target.value))}>
          {partitionOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <Box flex={1} />
        <Button variant="contained" startIcon={<AddIcon />} disabled={!isOnline} onClick={openNewType}>工種を追加</Button>
      </Stack>

      <Paper variant="outlined">
        <Box sx={{ overflowX: 'auto' }}>
          {visibleWorkTypes.length === 0 && (
            <EmptyState message='工種が登録されていません。「工種を追加」から作成してください。' py={4} />
          )}
          {visibleWorkTypes.map((wt) => {
            const subs = subtypesOf(wt.id);
            const allDetails = subs.flatMap((s) => detailsOf(s.id));
            const totalDetails = allDetails.length;
            const totalMixRatios = new Set(allDetails.map((d) => d.mix_ratio_definition_id).filter((x) => x != null)).size;
            const wtMissingMix = workTypeMissingMixRatio(wt);
            const wtNoChildren = subs.length === 0;
            return (
              <Accordion key={wt.id} disableGutters
                expanded={expandedWorkType === wt.id}
                onChange={() => setExpandedWorkType((prev) => prev === wt.id ? null : wt.id)}>
                <MasterAccordionSummary
                  wrap
                  idLabel={`#${wt.id}`}
                  title={<Typography variant="body2" fontWeight={600} sx={{ minWidth: 220 }}>{wt.name}</Typography>}
                  chips={<>
                    <Chip size="small" color="primary" variant="outlined" label={`${subs.length} 種別`} />
                    <Chip size="small" color="secondary" variant="outlined" label={`${totalDetails} 工程`} />
                    <Chip size="small" color="success" variant="outlined" label={`${totalMixRatios} 配合比`} />
                    {wtNoChildren && <Chip size="small" color="error" icon={<WarningAmberIcon />} label="種別未登録" />}
                    {!wtNoChildren && wtMissingMix && <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="配下に配合比未確定あり" />}
                    <Box flex={1} />
                  </>}
                  actions={<>
                    <IconButton size="small" title="編集" disabled={!isOnline} onClick={() => openEditType(wt)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" title="複製" disabled={!isOnline} onClick={() => duplicateType(wt)}><ContentCopyIcon fontSize="small" /></IconButton>
                    <IconButton size="small" title="削除" disabled={!isOnline} onClick={() => setConfirmDeleteType(wt)}><DeleteIcon fontSize="small" /></IconButton>
                  </>}
                />
                <AccordionDetails sx={{ backgroundColor: 'grey.50', px: 2 }}>
                  <Stack gap={1.5}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" color="text.secondary">配下の種別 ({subs.length} 件)</Typography>
                      <Box flex={1} />
                      <Button size="small" startIcon={<AddIcon />} disabled={!isOnline} onClick={() => openNewSubtype(wt.id)}>種別を追加</Button>
                    </Stack>
                    {subs.length === 0 && (
                      <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="caption">配下の種別が未登録です</Typography>
                      </Box>
                    )}
                    {subs.map((wst) => {
                      const details = detailsOf(wst.id);
                      const wstMixRatios = new Set(details.map((d) => d.mix_ratio_definition_id).filter((x) => x != null)).size;
                      const wstMissingMix = subtypeMissingMixRatio(wst);
                      const wstNoChildren = details.length === 0;
                      return (
                        <Accordion key={wst.id} disableGutters variant="outlined" sx={{ backgroundColor: 'background.paper' }}
                          expanded={expandedSubtype === wst.id}
                          onChange={() => setExpandedSubtype((prev) => prev === wst.id ? null : wst.id)}>
                          <MasterAccordionSummary
                            wrap
                            idLabel={`#${wst.id}`}
                            title={<Typography variant="body2" sx={{ minWidth: 200 }}>{wst.name}</Typography>}
                            chips={<>
                              <Chip size="small" variant="outlined" label={wst.unit} />
                              <Chip size="small" color="secondary" variant="outlined" label={`${details.length} 工程`} />
                              <Chip size="small" color="success" variant="outlined" label={`${wstMixRatios} 配合比`} />
                              {wstNoChildren && <Chip size="small" color="error" icon={<WarningAmberIcon />} label="工程未登録" />}
                              {!wstNoChildren && wstMissingMix && <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="配下に配合比未確定あり" />}
                              <Box flex={1} />
                            </>}
                            actions={<>
                              <IconButton size="small" title="編集" disabled={!isOnline} onClick={() => openEditSubtype(wst)}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" title="複製" disabled={!isOnline} onClick={() => duplicateSubtype(wst)}><ContentCopyIcon fontSize="small" /></IconButton>
                              <IconButton size="small" title="削除" disabled={!isOnline} onClick={() => setConfirmDeleteSubtype(wst)}><DeleteIcon fontSize="small" /></IconButton>
                            </>}
                          />
                          <AccordionDetails sx={{ backgroundColor: 'grey.100' }}>
                            <Stack gap={1}>
                              <Stack direction="row" alignItems="center" gap={1}>
                                <Typography variant="caption" color="text.secondary">配下の工程 ({details.length} 件)</Typography>
                                <Box flex={1} />
                                <Button size="small" startIcon={<AddIcon />} disabled={!isOnline} onClick={() => openNewDetail(wst.id)}>工程を追加</Button>
                              </Stack>
                              <Paper variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold', width: 60 }}>ID</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>工程名</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>配合比 (必須)</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', width: 140 }}>操作</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {details.map((wd) => (
                                      <TableRow key={wd.id}>
                                        <TableCell>{wd.id}</TableCell>
                                        <TableCell>{wd.name}</TableCell>
                                        <TableCell>
                                          {wd.mix_ratio_definition_id != null
                                            ? <Chip size="small" color="success" variant="outlined" icon={<ScienceIcon />}
                                                label={mixRatioLabel(db, wd.mix_ratio_definition_id)} />
                                            : <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="配合比未確定" />}
                                        </TableCell>
                                        <TableCell>
                                          <IconButton size="small" title="編集" disabled={!isOnline} onClick={() => openEditDetail(wd)}><EditIcon fontSize="small" /></IconButton>
                                          <IconButton size="small" title="複製" disabled={!isOnline} onClick={() => duplicateDetail(wd)}><ContentCopyIcon fontSize="small" /></IconButton>
                                          <IconButton size="small" title="削除" disabled={!isOnline} onClick={() => setConfirmDeleteDetail(wd)}><DeleteIcon fontSize="small" /></IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {details.length === 0 && (
                                      <TableRow><TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>工程が未登録です</TableCell></TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </Paper>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Paper>

      {/* ===== 編集ダイアログ ===== */}
      {editingType && (
        <WorkTypeDialog
          draft={editingType} isNew={editingTypeIsNew}
          isOnline={isOnline}
          onSave={saveType} onClose={() => setEditingType(null)}
        />
      )}
      {editingSubtype && (
        <WorkSubtypeDialog
          draft={editingSubtype} isNew={editingSubtypeIsNew}
          isOnline={isOnline}
          workTypes={visibleWorkTypes}
          workDetails={allWorkDetails}
          onSave={saveSubtype} onClose={() => setEditingSubtype(null)}
        />
      )}
      {editingDetail && (
        <WorkDetailDialog
          draft={editingDetail} isNew={editingDetailIsNew}
          isOnline={isOnline}
          db={db}
          workTypes={visibleWorkTypes} workSubtypes={allWorkSubtypes.filter((s) => visibleWorkTypes.some((wt) => wt.id === s.work_type_id))}
          onSave={saveDetail} onClose={() => setEditingDetail(null)}
        />
      )}

      {/* ===== 削除確認 ===== */}
      {confirmDeleteType !== null && (() => {
        const target = confirmDeleteType;
        return (
          <Dialog open onClose={() => setConfirmDeleteType(null)}>
            <DialogTitle>工種「{target.name}」を削除しますか？</DialogTitle>
            <DialogContent>
              <Typography variant="body2">配下の種別・工程もすべて削除されます（論理削除）。</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDeleteType(null)}>キャンセル</Button>
              <Button color="error" variant="contained" disabled={!isOnline} onClick={() => deleteType(target)}>削除</Button>
            </DialogActions>
          </Dialog>
        );
      })()}
      {confirmDeleteSubtype !== null && (() => {
        const target = confirmDeleteSubtype;
        return (
          <Dialog open onClose={() => setConfirmDeleteSubtype(null)}>
            <DialogTitle>種別「{target.name}」を削除しますか？</DialogTitle>
            <DialogContent>
              <Typography variant="body2">配下の工程もすべて削除されます（論理削除）。</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDeleteSubtype(null)}>キャンセル</Button>
              <Button color="error" variant="contained" disabled={!isOnline} onClick={() => deleteSubtype(target)}>削除</Button>
            </DialogActions>
          </Dialog>
        );
      })()}
      {confirmDeleteDetail !== null && (() => {
        const target = confirmDeleteDetail;
        return (
          <Dialog open onClose={() => setConfirmDeleteDetail(null)}>
            <DialogTitle>工程「{target.name}」を削除しますか？</DialogTitle>
            <DialogActions>
              <Button onClick={() => setConfirmDeleteDetail(null)}>キャンセル</Button>
              <Button color="error" variant="contained" disabled={!isOnline} onClick={() => deleteDetail(target)}>削除</Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Stack>
  );
}
